/**
 * Custom hooks for scheduling data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffApi, schedulingApi, weatherApi, conflictsApi, leaveApi } from '../lib/api';
import { useToast } from '@/hooks/use-toast';

// Staff Hooks
export function useStaff() {
  return useQuery({
    queryKey: ['scheduling', 'staff'],
    queryFn: staffApi.getAll,
  });
}

export function useStaffById(id: number) {
  return useQuery({
    queryKey: ['scheduling', 'staff', id],
    queryFn: () => staffApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: staffApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'staff'] });
      toast({ title: 'Staff member created successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create staff member',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => staffApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'staff'] });
      toast({ title: 'Staff member updated successfully' });
    },
  });
}

// Job Schedule Hooks
export function useSchedules(params?: any) {
  return useQuery({
    queryKey: ['scheduling', 'jobs', params],
    queryFn: () => schedulingApi.getAll(params),
  });
}

export function useScheduleById(id: number) {
  return useQuery({
    queryKey: ['scheduling', 'jobs', id],
    queryFn: () => schedulingApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: schedulingApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'jobs'] });
      toast({ title: 'Job scheduled successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create schedule',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => schedulingApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'jobs'] });
      toast({ title: 'Schedule updated successfully' });
    },
  });
}

export function useAssignStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ jobId, data }: { jobId: number; data: any }) => schedulingApi.assignStaff(jobId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'jobs'] });
      toast({ title: 'Staff assigned successfully' });
    },
  });
}

// Weather Hooks
export function useWeather(date: string) {
  return useQuery({
    queryKey: ['scheduling', 'weather', date],
    queryFn: () => weatherApi.get(date),
    enabled: !!date,
  });
}

export function useCreateWeather() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: weatherApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'weather'] });
      toast({ title: 'Weather note created' });
    },
  });
}

// Conflicts Hooks
export function useConflicts(params?: any) {
  return useQuery({
    queryKey: ['scheduling', 'conflicts', params],
    queryFn: () => conflictsApi.getAll(params),
  });
}

export function useResolveConflict() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: conflictsApi.resolve,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'conflicts'] });
      toast({ title: 'Conflict resolved' });
    },
  });
}

// Leave Management Hooks (Admin) - Simplified (no approval workflow)
export function useAllLeave(params?: any) {
  return useQuery({
    queryKey: ['scheduling', 'leave', params],
    queryFn: () => leaveApi.getAll(params),
  });
}

export function useStaffLeave(staffId: number) {
  return useQuery({
    queryKey: ['scheduling', 'leave', 'staff', staffId],
    queryFn: () => leaveApi.getByStaff(staffId),
    enabled: !!staffId,
  });
}

export function useAddLeave() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ staffId, data }: { staffId: number; data: any }) =>
      leaveApi.add(staffId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'leave'] });
      toast({ title: 'Annual leave added successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to add annual leave',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateLeave() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ leaveId, data }: { leaveId: number; data: any }) =>
      leaveApi.update(leaveId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'leave'] });
      toast({ title: 'Annual leave updated successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update leave',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteLeave() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: leaveApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduling', 'leave'] });
      toast({ title: 'Annual leave deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete leave',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
